import './SearchInput.scss';

import React, { useEffect, useRef } from 'react';
import { getPresetInputValue, submitTermSearch } from '../../helpers';

import PropTypes from 'prop-types';
import cx from 'classnames';
import tagging from '../../helpers/tagging';
import { useStateValue } from '../../state';

const SearchInput = React.forwardRef(
	(
		{
			getInputProps,
			setInputValue,
			openCbMenu,
			cbReset,
			enableButtons,
			searchResults,
			isOpen,
			onFocus,
			handleOverlayClick,
		},
		ref
	) => {
		const [
			{
				searchParamKey,
				_t,
				clientCode,
				isPopularSearchesEnabled,
				inputFocused,
			},
			dispatch,
		] = useStateValue();

		const [state] = useStateValue();
		const searchBtnRef = useRef(null);

		const handleInputFocus = () => {
			const value = ref.current.value;
			SearchInput.propTypes = {
				onFocus: PropTypes.func,
			};
			dispatch({
				type: 'setInputFocused',
				inputFocused: true,
			});

			if (value.length) {
				openCbMenu();
			}

			if (isPopularSearchesEnabled) {
				openCbMenu();
				dispatch({ type: 'showPopularSearches', showPopularSearches: true });
			}

				onFocus();
		};

		const handleClearInput = () => {
			setInputValue('');

			cbReset();

			// onFocus triggers before setInputValue so force local value
			ref.current.value = '';
			ref.current.focus();
		};

		const handleSubmitSearch = () => {
			if (!ref.current.value) return;

			let suggestedProducts;

			if (searchResults && searchResults.length) {
				suggestedProducts = searchResults.filter(
					(result) => result.PredictiveSearchType === 'Product'
				);
			}

			tagging.initTagging({ ...state, suggestedProducts });

			submitTermSearch(clientCode, ref.current.value);
		};

		useEffect(() => {
			const presetInputValue = getPresetInputValue();

			if (presetInputValue) {
				setInputValue(presetInputValue);
			}
		}, []);

		const inputProps = getInputProps({
			ref,
		});

		const hasResults = isOpen && searchResults && searchResults.length;
		const borderColor =
			isOpen || inputFocused ? 'border-cta-main' : 'border-ongreen';

		const currentSearchTerm = ref?.current?.value ?? '';

		return (
			<div
				data-tag-section="sitesearch"
				data-testid="siteSearch-form"
				className={cx(
					'siteSearch__input-container',
					'box-border',
					'overflow-hidden',
					'relative',
					'flex',
					'flex-1',
					'border-4 border-solid rounded-l-lg rounded-r-lg',
					inputFocused
						? 'border-lightgreen'
						: 'border-transparent'
				)}
				role="combobox"
				aria-haspopup="listbox"
				aria-owns="siteSearch-menu"
				aria-expanded={isOpen}
			>
				<input
					className={cx(
						'siteSearch__input box-border c-input-text u-w--100 text-body px-4 py-0 bg-transparent md:static',
						'border-gray-40 border-r-0 rounded-tl-lg rounded-bl-lg prominent-border flex-1',
						isOpen && searchResults?.length && 'u-search-isOpen'
					)}
					id="siteSearchInput"
					{...inputProps}
					onFocus={handleInputFocus}
					onBlur={(e) => {
						dispatch({ type: 'setInputFocused', inputFocused: false });
						inputProps.onBlur(e);
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							searchBtnRef.current.click();
						}
						if (e.key === 'Tab') {
							handleOverlayClick();
						}
						inputProps.onKeyDown(e);
					}}
					data-testid="siteSearch-input"
					type="search"
					name={searchParamKey}
					placeholder={_t.placeholder}
				/>

				{enableButtons ? (
					<button
						type="button"
						aria-label="Clear search input"
						data-testid="siteSearch-clear-button"
						className={cx(
							'siteSearch__clear-button box-border p-0 text-tertiary mr-0 bg-transparent flex flex-col items-center justify-center',
							' border-solid border-b border-t border-l-0 border-r-0',
							'border-gray-40 -ml-1 my-0'
						)}
						onClick={handleClearInput}>
						<span
							className={cx(
								'ico ico-close',
								'text-disabled'
							)}></span>
					</button>
				) : null}

				<button
					data-tag-name="submit search"
					data-tag-searchterm={currentSearchTerm}
					type="button"
					aria-label="Submit search"
					onClick={handleSubmitSearch}
					data-testid="siteSearch-submit-button"
					ref={searchBtnRef}
					className={cx(
						'siteSearch__search-button',
						'box-border',
						'flex items-center justify-center',
						'border border-solid',
						'm-0',
						'before-hidden',
						'text-white',
						'icon-',
						'static',
						'lg:pr-3 lg:pl-3 bg-cta-main hover:bg-cta-mainhover',
						(isOpen || inputFocused)
							? 'bg-cta-main border-gray-40'
							: 'bg-ongreen',
						!hasResults
							? 'prominent-border-button'
							: '',
						hasResults
							? 'prominent-border-button'
							: '',
						borderColor,
					)}>
					<span
						className={cx(
							'ico ico-search leading-none static',
							'lg:pr-2'
						)}
						style={{ fontSize: '0.75em' }}
					/>
						<div className=" static text-title-sm text-white hidden lg:block flex flex-shrink-0 mb-0">
							{_t.buttonLabel}
						</div>
				</button>
			</div>
		);
	}
);

SearchInput.propTypes = {
	getInputProps: PropTypes.func,
	setInputValue: PropTypes.func,
	openCbMenu: PropTypes.func,
	cbReset: PropTypes.func,
	enableButtons: PropTypes.bool,
	searchResults: PropTypes.array,
	isOpen: PropTypes.bool,
};

export default SearchInput;
